import React, { useContext, ReactNode, useState, useCallback, useEffect } from 'react';
import { Box, createTheme, CssBaseline, GlobalStyles, PaletteMode, ThemeProvider, Theme, ThemeOptions, useMediaQuery } from '@mui/material';
import { useLocalStorage } from 'hooks';
import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';

// Augment the palette to include a salmon color
declare module '@mui/material/styles' {
    interface Palette {
        listColor: Palette['primary'];
    }

    interface PaletteOptions {
        listColor?: PaletteOptions['primary'];
    }
}

// Update the Button's color options to include a salmon option
declare module '@mui/material/Button' {
    interface ButtonPropsColorOverrides {
        listColor: true;
    }
}

interface CustomThemeContextType extends Theme {
    setThemeMode: (mode: PaletteMode) => void;
    setCustomThemes: (lightTheme?: ThemeOptions, darkTheme?: ThemeOptions, defaultTheme?: 'dark' | 'light' | 'system') => void;
}

const CustomThemeContext = React.createContext<CustomThemeContextType | null | undefined>(null);

export function useCustomTheme() {
    return useContext(CustomThemeContext)!;
}

export function CustomThemeProvider({ children }: { children?: ReactNode | undefined }) {
    const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
    const [themeMode, setMode] = useLocalStorage<PaletteMode>('avron-theme-mode', prefersDarkMode ? 'dark' : 'light');

    let theme = createTheme({
        // Theme customization goes here as usual, including tonalOffset and/or
        // contrastThreshold as the augmentColor() function relies on these
    });

    const defaultLightTheme = createTheme({
        palette: {
            mode: 'light',
            listColor: theme.palette.augmentColor({
                color: {
                    main: '#24b1b1'
                },
                name: 'listColor'
            })
        }
    });

    const defaultDarkTheme = createTheme({
        palette: {
            mode: 'dark',
            background: {
                default: '#1e1e1e'
            },
            listColor: theme.palette.augmentColor({
                color: {
                    main: '#008080'
                },
                name: 'listColor'
            })
        }
    });

    const [lightTheme, setLightTheme] = useState<Theme>(defaultLightTheme);
    const [darkTheme, setDarkTheme] = useState<Theme>(defaultDarkTheme);
    const [currentTheme, setCurrentTheme] = useState<Theme>(defaultDarkTheme);

    useEffect(() => {
        if (themeMode === 'light') {
            setCurrentTheme(lightTheme);
        } else {
            setCurrentTheme(darkTheme);
        }
    },[darkTheme, lightTheme, themeMode])

    const setThemeMode = useCallback((mode: PaletteMode) => {
        setMode(mode);
    }, [setMode]);

    const setCustomThemes = useCallback((newLightTheme?: ThemeOptions, newDarkTheme?: ThemeOptions, defaultTheme?: 'dark' | 'light' | 'system') => {
        if (newLightTheme) {
            const resultingLightTheme = merge(cloneDeep(lightTheme), newLightTheme);
            setLightTheme(createTheme(resultingLightTheme));
        }
        if (newDarkTheme) {
            const resultingDarkTheme = merge(cloneDeep(darkTheme), newDarkTheme);
            setDarkTheme(createTheme(resultingDarkTheme));
        }
        if (window.localStorage.getItem('avron-theme-mode')) {
            if (!defaultTheme || defaultTheme === 'system') {
                setThemeMode(prefersDarkMode ? 'dark' : 'light');
            } else {
                setThemeMode(defaultTheme);
            }
        }
    }, [darkTheme, lightTheme, prefersDarkMode, setThemeMode]); 


    const contextValue: CustomThemeContextType = {
        ...currentTheme,
        setThemeMode,
        setCustomThemes
    };

    return (
        <CustomThemeContext.Provider value={contextValue}>
            <ThemeProvider theme={currentTheme}>
                <CssBaseline />
                <GlobalStyles
                    styles={(theme) => ({
                        html: {
                            minHeight: '100%'
                        },
                        body: {
                            minHeight: '100vh',
                            backgroundColor: theme.palette.background.default,
                            color: theme.palette.text.primary
                        },
                        '#root': {
                            minHeight: '100vh',
                            backgroundColor: theme.palette.background.default,
                            color: theme.palette.text.primary
                        }
                    })}
                />
                <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary' }}>
                    {children}
                </Box>
            </ThemeProvider>
        </CustomThemeContext.Provider>
    );
}
